import React, { useState } from 'react';
import { UserProfile, UserRole, AccountTier, BanRecord, AuditLogRecord } from '../types/user';
import { StoredKundaliRecord, banUserAdmin, unbanUserAdmin, blacklistIdentifierAdmin } from '../lib/kundaliService';
import { Language } from '../types/kundali';
import { formatDateTime } from '../utils/dateUtils';
import {
  Search,
  CheckCircle2,
  AlertTriangle,
  KeyRound,
  Trash2,
  Ban,
  ShieldAlert,
  ShieldCheck,
  PhoneOff,
  MailX,
  X,
  Plus,
  RefreshCw,
  UserX,
  AlertOctagon,
  Shield,
  Save,
  ArrowRight
} from 'lucide-react';

interface UserManagementViewProps {
  currentUser: UserProfile;
  usersList: UserProfile[];
  bannedEntities: BanRecord[];
  kundalis: StoredKundaliRecord[];
  auditLogs: AuditLogRecord[];
  onRefresh: () => void;
  onShowToast: (msg: string) => void;
  onResetPassword: (user: UserProfile) => void;
  onDeleteUser: (user: UserProfile) => void;
  onUpdateRoleAndTier: (
    user: UserProfile,
    newRole: UserRole,
    newTier: AccountTier,
    practice: boolean,
    consultation: boolean
  ) => void;
  onUpdateQuota: (user: UserProfile, quota: number) => void;
  activeLang?: Language;
}

export const UserManagementView: React.FC<UserManagementViewProps> = ({
  currentUser,
  usersList,
  bannedEntities,
  kundalis,
  auditLogs,
  onRefresh,
  onShowToast,
  onResetPassword,
  onDeleteUser,
  onUpdateRoleAndTier,
  onUpdateQuota,
  activeLang = 'en'
}) => {
  const [subTab, setSubTab] = useState<'users' | 'blacklist'>('users');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTierFilter, setSelectedTierFilter] = useState<string>('all');
  
  // Staged changes for role and tier across multiple users
  const [stagedRoles, setStagedRoles] = useState<Record<string, UserRole>>({});
  const [stagedTiers, setStagedTiers] = useState<Record<string, AccountTier>>({});

  // Batch Confirmation Modal state
  const [batchModalOpen, setBatchModalOpen] = useState(false);

  // Ban User Modal state
  const [banTargetUser, setBanTargetUser] = useState<UserProfile | null>(null);
  const [banReason, setBanReason] = useState('Violation of platform Terms of Service');
  const [blacklistEmail, setBlacklistEmail] = useState(true);
  const [blacklistPhone, setBlacklistPhone] = useState(true);
  const [isBanning, setIsBanning] = useState(false);

  // Preemptive Blacklist Modal state
  const [preemptiveModalOpen, setPreemptiveModalOpen] = useState(false);
  const [preemptiveType, setPreemptiveType] = useState<'email' | 'phone'>('phone');
  const [preemptiveIdentifier, setPreemptiveIdentifier] = useState('');
  const [preemptiveReason, setPreemptiveReason] = useState('Preventive fraud and abuse protection');
  const [isBlacklisting, setIsBlacklisting] = useState(false);

  // Preset reasons for quick selection
  const BAN_PRESETS = [
    'Violation of platform Terms of Service',
    'Spam / Repeated Abusive Behavior',
    'Payment Fraud or Unauthorized Reversal',
    'Malicious Scripting / Scraping Attempt',
    'Impersonation of Consulting Astrologer'
  ];

  // Execute Ban on user
  const handleExecuteBan = async () => {
    if (!banTargetUser) return;
    if (!banReason.trim()) {
      onShowToast('Please provide a specific reason for administrative ban.');
      return;
    }

    setIsBanning(true);
    try {
      await banUserAdmin(
        banTargetUser.uid,
        blacklistEmail ? banTargetUser.email : '',
        blacklistPhone ? banTargetUser.mobile : undefined,
        banReason.trim(),
        currentUser.uid || currentUser.email,
        currentUser
      );
      onShowToast(`Enforced account ban and blacklisted identifiers for ${banTargetUser.email}`);
      setBanTargetUser(null);
      setBanReason('Violation of platform Terms of Service');
      onRefresh();
    } catch (err: any) {
      onShowToast(err.message || 'Failed to ban user');
    } finally {
      setIsBanning(false);
    }
  };

  // Lift Ban
  const handleLiftBan = async (userId: string, email?: string, phone?: string) => {
    try {
      await unbanUserAdmin(userId, email, phone, currentUser.uid || currentUser.email, currentUser);
      onShowToast(`Ban revoked successfully for ${email || phone || userId}`);
      onRefresh();
    } catch (err: any) {
      onShowToast(err.message || 'Failed to revoke ban');
    }
  };

  // Execute Preemptive Blacklist
  const handleExecutePreemptiveBlacklist = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!preemptiveIdentifier.trim()) {
      onShowToast('Please enter a valid phone number or email address.');
      return;
    }
    if (!preemptiveReason.trim()) {
      onShowToast('Please enter a reason for blacklisting.');
      return;
    }

    setIsBlacklisting(true);
    try {
      await blacklistIdentifierAdmin(
        preemptiveIdentifier.trim(),
        preemptiveType,
        preemptiveReason.trim(),
        currentUser.uid || currentUser.email,
        currentUser
      );
      onShowToast(`Blacklisted ${preemptiveType}: ${preemptiveIdentifier.trim()}`);
      setPreemptiveModalOpen(false);
      setPreemptiveIdentifier('');
      setPreemptiveReason('Preventive fraud and abuse protection');
      onRefresh();
    } catch (err: any) {
      onShowToast(err.message || 'Failed to blacklist identifier');
    } finally {
      setIsBlacklisting(false);
    }
  };

  // Compute modified users list
  const modifiedUserIds = Array.from(
    new Set([...Object.keys(stagedRoles), ...Object.keys(stagedTiers)])
  ).filter((uid) => {
    const u = usersList.find((item) => item.uid === uid);
    if (!u) return false;
    const r = stagedRoles[uid] !== undefined ? stagedRoles[uid] : u.role;
    const t = stagedTiers[uid] !== undefined ? stagedTiers[uid] : (u.tier || AccountTier.GUEST);
    return r !== u.role || t !== (u.tier || AccountTier.GUEST);
  });

  const handleCommitBatch = () => {
    modifiedUserIds.forEach((uid) => {
      const u = usersList.find((item) => item.uid === uid);
      if (!u) return;
      const newRole = stagedRoles[uid] !== undefined ? stagedRoles[uid] : u.role;
      const newTier = stagedTiers[uid] !== undefined ? stagedTiers[uid] : (u.tier || AccountTier.GUEST);
      onUpdateRoleAndTier(
        u,
        newRole,
        newTier,
        !!u.hasPracticeEntitlement,
        !!u.hasConsultationEntitlement
      );
    });
    setStagedRoles({});
    setStagedTiers({});
    setBatchModalOpen(false);
    onShowToast(`Successfully committed role & tier updates for ${modifiedUserIds.length} users.`);
  };

  const activeBlacklistCount = bannedEntities.filter((b) => b.isBanned !== false).length;

  return (
    <div id="user-management-view" className="space-y-4 relative">
      {/* FIXED TOP SAVE BAR (Appears when there are staged changes) */}
      {modifiedUserIds.length > 0 && (
        <div className="sticky top-0 z-40 bg-amber-50 border-2 border-amber-300 rounded-2xl px-4 py-3 shadow-lg flex items-center justify-between gap-4 animate-fade-in">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-xl bg-amber-600 text-white flex items-center justify-center font-bold text-xs shadow-xs animate-bounce">
              {modifiedUserIds.length}
            </div>
            <div>
              <h4 className="text-xs font-bold text-amber-900">
                Uncommitted Member Role & Tier Modifications
              </h4>
              <p className="text-[11px] text-amber-700">
                You have changed settings for {modifiedUserIds.length} user(s). Review and save to commit changes.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => {
                setStagedRoles({});
                setStagedTiers({});
              }}
              className="px-3 py-1.5 text-xs font-semibold text-amber-900 hover:bg-amber-100 rounded-xl transition-colors cursor-pointer"
            >
              Discard Changes
            </button>
            <button
              id="btn-review-commit-all"
              type="button"
              onClick={() => setBatchModalOpen(true)}
              className="flex items-center gap-1.5 px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white text-xs font-bold rounded-xl shadow-md transition-all cursor-pointer"
            >
              <Save className="w-4 h-4" />
              <span>Review & Save All ({modifiedUserIds.length})</span>
            </button>
          </div>
        </div>
      )}

      {/* Top Header & Sub-Navigation */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-3 border-b border-astro-border">
        <div>
          <div className="flex items-center gap-2">
            <Shield className="w-4 h-4 text-astro-primary" />
            <h3 className="text-sm font-bold text-astro-primary">
              User Administration & Enforcement Suite
            </h3>
          </div>
          <p className="text-xs text-[#738280]">
            Manage member accounts, RBAC matrix, quotas, and strict Mobile/Email blacklists. Bulk changes are supported via fixed top save.
          </p>
        </div>

        {/* Action Controls & Sub-tabs */}
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center bg-[#FAF6F0] p-1 rounded-xl border border-astro-border">
            <button
              id="subtab-active-users"
              type="button"
              onClick={() => setSubTab('users')}
              className={`px-3 py-1 text-xs font-semibold rounded-lg transition-all ${
                subTab === 'users'
                  ? 'bg-astro-primary text-white shadow-xs'
                  : 'text-astro-secondary-text hover:text-astro-primary'
              }`}
            >
              All Accounts ({usersList.length})
            </button>
            <button
              id="subtab-blacklist-registry"
              type="button"
              onClick={() => setSubTab('blacklist')}
              className={`px-3 py-1 text-xs font-semibold rounded-lg transition-all flex items-center gap-1.5 ${
                subTab === 'blacklist'
                  ? 'bg-[#B91C1C] text-white shadow-xs'
                  : 'text-astro-secondary-text hover:text-[#B91C1C]'
              }`}
            >
              <Ban className="w-3.5 h-3.5" />
              <span>Blacklist Registry ({activeBlacklistCount})</span>
            </button>
          </div>

          <button
            type="button"
            onClick={() => {
              setPreemptiveType('phone');
              setPreemptiveIdentifier('');
              setPreemptiveReason('Preventive fraud and abuse protection');
              setPreemptiveModalOpen(true);
            }}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-red-600 hover:bg-red-700 text-white text-xs font-bold transition-all shadow-xs cursor-pointer"
          >
            <Plus className="w-3.5 h-3.5" />
            <span>Preemptive Blacklist</span>
          </button>
        </div>
      </div>

      {/* Search Bar & Tier Filter Bar */}
      {subTab === 'users' && (
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 bg-white px-3 py-2 rounded-xl border border-astro-border shadow-2xs flex-1 max-w-md">
            <Search className="w-4 h-4 text-astro-muted" />
            <input
              type="text"
              placeholder="Search by email, name, phone or role..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full text-xs bg-transparent focus:outline-hidden text-astro-text"
            />
          </div>

          <div className="flex items-center gap-2 bg-white px-3 py-1.5 rounded-xl border border-astro-border shadow-2xs">
            <span className="text-xs font-semibold text-astro-muted">Tier Filter:</span>
            <select
              value={selectedTierFilter}
              onChange={(e) => setSelectedTierFilter(e.target.value)}
              className="text-xs font-bold bg-transparent text-astro-primary focus:outline-hidden cursor-pointer"
            >
              <option value="all">All Account Tiers</option>
              <option value={AccountTier.GUEST}>Guest</option>
              <option value={AccountTier.BASIC}>Basic</option>
              <option value={AccountTier.PRO_ASTRO}>Pro Astro</option>
              <option value={AccountTier.ACHARYA}>Acharya</option>
            </select>
          </div>
        </div>
      )}

      {/* SUB-TAB 1: USER ACCOUNTS TABLE */}
      {subTab === 'users' && (
        <div className="overflow-x-auto rounded-2xl border border-astro-border shadow-xs">
          <table className="w-full text-left text-xs border-collapse">
            <thead className="bg-[#FAF6F0] text-astro-secondary-text font-bold border-b border-astro-border">
              <tr>
                <th className="p-3">User & Contact</th>
                <th className="p-3">Role Authority</th>
                <th className="p-3">Account Tier</th>
                <th className="p-3">Kundali Quota</th>
                <th className="p-3">Status / Ban Enforcement</th>
                <th className="p-3">Entitlements</th>
                <th className="p-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-astro-border bg-white">
              {usersList
                .filter((u) => {
                  const q = searchTerm.toLowerCase();
                  const matchesSearch =
                    u.email?.toLowerCase().includes(q) ||
                    u.displayName?.toLowerCase().includes(q) ||
                    u.mobile?.includes(q) ||
                    u.role?.toLowerCase().includes(q);

                  const currentTier = stagedTiers[u.uid] !== undefined ? stagedTiers[u.uid] : (u.tier || AccountTier.GUEST);
                  const matchesTier = selectedTierFilter === 'all' || currentTier === selectedTierFilter;

                  return matchesSearch && matchesTier;
                })
                .map((u) => {
                  const isSelf = u.uid === currentUser.uid;
                  const createdKundalisCount = kundalis.filter(
                    (k) => k.userId === u.uid || k.userEmail?.toLowerCase() === u.email?.toLowerCase()
                  ).length;
                  const quotaLimit = u.kundaliQuota || 10;
                  const isUserBanned = !!u.isBanned;

                  const currentRole = stagedRoles[u.uid] !== undefined ? stagedRoles[u.uid] : (u.role || UserRole.BASIC_USER);
                  const currentTier = stagedTiers[u.uid] !== undefined ? stagedTiers[u.uid] : (u.tier || AccountTier.GUEST);
                  const isRowModified = (stagedRoles[u.uid] !== undefined && stagedRoles[u.uid] !== u.role) || (stagedTiers[u.uid] !== undefined && stagedTiers[u.uid] !== (u.tier || AccountTier.GUEST));

                  return (
                    <tr
                      key={u.uid}
                      id={`user-row-${u.uid}`}
                      className={`hover:bg-[#FBF9F5] transition-colors ${
                        isRowModified ? 'bg-amber-50/30' : isUserBanned ? 'bg-red-50/40' : ''
                      }`}
                    >
                      {/* User Info */}
                      <td className="p-3">
                        <div className="flex items-center gap-1.5">
                          <span className="font-semibold text-astro-text">
                            {u.displayName || 'Unnamed Member'}
                          </span>
                          {isUserBanned && (
                            <span className="px-1.5 py-0.5 bg-red-100 text-red-800 text-[10px] font-bold rounded-md border border-red-200">
                              BANNED
                            </span>
                          )}
                          {isRowModified && (
                            <span className="px-1.5 py-0.5 bg-amber-100 text-amber-800 text-[10px] font-bold rounded-md border border-amber-200 animate-pulse">
                              STAGED
                            </span>
                          )}
                        </div>
                        <div className="text-[11px] text-astro-muted font-mono flex items-center gap-1">
                          <span>{u.email}</span>
                        </div>
                        {u.mobile && (
                          <div className="text-[11px] text-astro-muted font-mono flex items-center gap-1">
                            <span>📞 {u.mobile}</span>
                          </div>
                        )}
                        <div className="text-[10px] text-astro-muted">UID: {u.uid.substring(0, 10)}...</div>
                      </td>

                      {/* Role */}
                      <td className="p-3">
                        <select
                          value={currentRole}
                          onChange={(e) =>
                            setStagedRoles((prev) => ({ ...prev, [u.uid]: e.target.value as UserRole }))
                          }
                          disabled={isSelf && u.role === UserRole.SUPER_ADMIN}
                          className="px-2.5 py-1 text-xs font-semibold bg-white border border-[#D5C7B2] rounded-lg focus:ring-1 focus:ring-astro-primary"
                        >
                          <option value={UserRole.SUPER_ADMIN}>SUPER_ADMIN</option>
                          <option value={UserRole.ADMIN}>ADMIN</option>
                          <option value={UserRole.CONSULTING_ASTROLOGER}>CONSULTING_ASTROLOGER</option>
                          <option value={UserRole.PRO_ASTROLOGER}>PRO_ASTROLOGER</option>
                          <option value={UserRole.NOVICE_ASTROLOGER}>NOVICE_ASTROLOGER</option>
                          <option value={UserRole.ADVANCED_ASTROLOGY_USER}>ADVANCED_ASTROLOGY_USER</option>
                          <option value={UserRole.BASIC_USER}>BASIC_USER</option>
                          <option value={UserRole.GUEST}>GUEST</option>
                        </select>
                      </td>

                      {/* Tier */}
                      <td className="p-3">
                        <select
                          value={currentTier}
                          onChange={(e) =>
                            setStagedTiers((prev) => ({ ...prev, [u.uid]: e.target.value as AccountTier }))
                          }
                          className="px-2 py-1 text-xs font-semibold bg-white border border-[#D5C7B2] rounded-lg focus:ring-1 focus:ring-astro-primary"
                        >
                          <option value={AccountTier.GUEST}>Guest</option>
                          <option value={AccountTier.BASIC}>Basic</option>
                          <option value={AccountTier.PRO_ASTRO}>Pro Astro</option>
                          <option value={AccountTier.ACHARYA}>Acharya</option>
                        </select>
                      </td>

                      {/* Quota */}
                      <td className="p-3 space-y-1">
                        <div className="flex items-center gap-1 text-xs font-bold text-astro-primary">
                          <span>{createdKundalisCount} Used</span>
                          <span className="text-astro-muted">/</span>
                          <span className="bg-[#FAF6F0] px-1.5 py-0.5 rounded border border-astro-border">
                            Quota: {quotaLimit}
                          </span>
                        </div>
                        <div className="flex items-center gap-1">
                          <button
                            type="button"
                            onClick={() => onUpdateQuota(u, quotaLimit + 50)}
                            className="px-1.5 py-0.5 bg-astro-primary hover:bg-astro-secondary text-white text-[10px] font-bold rounded"
                          >
                            +50
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              const val = prompt(`Set new Kundali generation quota for ${u.email}:`, String(quotaLimit));
                              if (val && !isNaN(Number(val))) {
                                onUpdateQuota(u, Number(val));
                              }
                            }}
                            className="px-1.5 py-0.5 bg-astro-card hover:bg-astro-border text-astro-primary text-[10px] font-bold rounded border border-astro-border"
                          >
                            Custom
                          </button>
                        </div>
                      </td>

                      {/* Status / Ban Diagnostic */}
                      <td className="p-3">
                        {isUserBanned ? (
                          <div className="space-y-1">
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-100 text-red-800 rounded-md text-[10px] font-bold border border-red-300">
                              <Ban className="w-3 h-3 text-red-600" />
                              <span>Blacklisted & Banned</span>
                            </span>
                            <div className="text-[10px] text-red-700 font-medium max-w-[180px] truncate" title={u.banReason}>
                              {u.banReason || 'Administrative Ban'}
                            </div>
                            {u.bannedAt && (
                              <div className="text-[9px] text-astro-muted">
                                {formatDateTime(u.bannedAt)}
                              </div>
                            )}
                          </div>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-50 text-emerald-800 rounded-md text-[10px] font-bold border border-emerald-200">
                            <CheckCircle2 className="w-3 h-3 text-emerald-600" />
                            <span>Active Account</span>
                          </span>
                        )}
                      </td>

                      {/* Entitlements */}
                      <td className="p-3 space-y-1">
                        <label className="flex items-center gap-1.5 text-[11px] font-medium text-astro-secondary-text cursor-pointer">
                          <input
                            type="checkbox"
                            checked={!!u.hasPracticeEntitlement}
                            onChange={(e) =>
                              onUpdateRoleAndTier(
                                u,
                                u.role,
                                u.tier || AccountTier.GUEST,
                                e.target.checked,
                                !!u.hasConsultationEntitlement
                              )
                            }
                            className="rounded text-astro-primary focus:ring-astro-primary"
                          />
                          <span>Practice Library</span>
                        </label>
                        <label className="flex items-center gap-1.5 text-[11px] font-medium text-astro-secondary-text cursor-pointer">
                          <input
                            type="checkbox"
                            checked={!!u.hasConsultationEntitlement}
                            onChange={(e) =>
                              onUpdateRoleAndTier(
                                u,
                                u.role,
                                u.tier || AccountTier.GUEST,
                                !!u.hasPracticeEntitlement,
                                e.target.checked
                              )
                            }
                            className="rounded text-astro-primary focus:ring-astro-primary"
                          />
                          <span>Consultation</span>
                        </label>
                      </td>

                      {/* Actions */}
                      <td className="p-3 text-right space-x-1.5 whitespace-nowrap">
                        {/* Ban / Lift Ban Toggle */}
                        {!isSelf && (
                          isUserBanned ? (
                            <button
                              id={`btn-unban-${u.uid}`}
                              type="button"
                              onClick={() => handleLiftBan(u.uid, u.email, u.mobile)}
                              title="Lift Ban and Restore Account Access"
                              className="p-1.5 text-emerald-700 bg-emerald-50 hover:bg-emerald-100 rounded-lg transition-colors border border-emerald-300 inline-flex"
                            >
                              <ShieldCheck className="w-4 h-4" />
                            </button>
                          ) : (
                            <button
                              id={`btn-ban-${u.uid}`}
                              type="button"
                              onClick={() => {
                                setBanTargetUser(u);
                                setBanReason('Violation of platform Terms of Service');
                                setBlacklistEmail(true);
                                setBlacklistPhone(!!u.mobile);
                              }}
                              title="Ban Account & Blacklist Phone/Email"
                              className="p-1.5 text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-colors border border-red-200 inline-flex"
                            >
                              <Ban className="w-4 h-4" />
                            </button>
                          )
                        )}

                        {/* Reset Password */}
                        <button
                          id={`btn-reset-pwd-${u.uid}`}
                          type="button"
                          onClick={() => onResetPassword(u)}
                          title="Reset User Password"
                          className="p-1.5 text-amber-700 bg-amber-50 hover:bg-amber-100 rounded-lg transition-colors border border-amber-200 inline-flex"
                        >
                          <KeyRound className="w-4 h-4" />
                        </button>

                        {/* Delete User Account */}
                        {!isSelf && (
                          <button
                            id={`btn-delete-user-${u.uid}`}
                            type="button"
                            onClick={() => onDeleteUser(u)}
                            title="Delete User Account"
                            className="p-1.5 text-red-700 bg-red-50 hover:bg-red-100 rounded-lg transition-colors border border-red-200 inline-flex"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      )}

      {/* SUB-TAB 2: BLACKLIST REGISTRY */}
      {subTab === 'blacklist' && (
        <div className="space-y-4">
          <div className="p-4 rounded-2xl border border-red-200 bg-red-50/50 flex items-center justify-between">
            <div className="space-y-0.5">
              <h4 className="text-xs font-bold text-red-900 flex items-center gap-1.5">
                <ShieldAlert className="w-4 h-4 text-red-600" />
                <span>Active Administrative Blacklist Registry ({bannedEntities.length})</span>
              </h4>
              <p className="text-[11px] text-red-700">
                Any phone number or email address listed here is instantly blocked from authentication and sign-up across the platform.
              </p>
            </div>
            <button
              type="button"
              onClick={() => {
                setPreemptiveType('phone');
                setPreemptiveIdentifier('');
                setPreemptiveReason('Preventive fraud and abuse protection');
                setPreemptiveModalOpen(true);
              }}
              className="px-3 py-1.5 rounded-xl bg-red-600 hover:bg-red-700 text-white text-xs font-bold shadow-xs cursor-pointer"
            >
              + Add Identifier
            </button>
          </div>

          <div className="overflow-x-auto rounded-2xl border border-astro-border shadow-xs bg-white">
            <table className="w-full text-left text-xs border-collapse">
              <thead className="bg-[#FAF6F0] text-astro-secondary-text font-bold border-b border-astro-border">
                <tr>
                  <th className="p-3">Identifier (Email / Phone)</th>
                  <th className="p-3">Reason for Blacklist</th>
                  <th className="p-3">Banned By Admin</th>
                  <th className="p-3">Timestamp</th>
                  <th className="p-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-astro-border">
                {bannedEntities.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="p-8 text-center text-astro-muted text-xs">
                      No banned entities or blacklisted identifiers found in registry.
                    </td>
                  </tr>
                ) : (
                  bannedEntities.map((b) => (
                    <tr key={b.userId || b.email} className="hover:bg-red-50/20 transition-colors">
                      <td className="p-3 font-mono font-bold text-red-900">
                        {b.email || b.phone || b.userId}
                      </td>
                      <td className="p-3 text-astro-text font-medium">
                        {b.reason || 'Administrative Ban'}
                      </td>
                      <td className="p-3 text-astro-muted font-mono text-[11px]">
                        {b.bannedBy || 'System Admin'}
                      </td>
                      <td className="p-3 text-astro-muted text-[11px]">
                        {b.bannedAt ? formatDateTime(b.bannedAt) : 'N/A'}
                      </td>
                      <td className="p-3 text-right">
                        <button
                          type="button"
                          onClick={() => handleLiftBan(b.userId, b.email, b.phone)}
                          className="px-2.5 py-1 bg-emerald-600 hover:bg-emerald-700 text-white text-[10px] font-bold rounded-lg shadow-2xs cursor-pointer"
                        >
                          Revoke Ban
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* BATCH CONFIRMATION PAGE / MODAL */}
      {batchModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs animate-fade-in">
          <div className="bg-astro-bg border border-astro-border rounded-3xl shadow-2xl max-w-2xl w-full p-6 space-y-4 overflow-hidden flex flex-col max-h-[85vh]">
            <div className="flex items-center gap-3 pb-3 border-b border-astro-border shrink-0">
              <div className="w-10 h-10 rounded-2xl bg-amber-100 text-amber-800 flex items-center justify-center font-bold">
                <ShieldCheck className="w-5 h-5 text-amber-700" />
              </div>
              <div>
                <h4 className="text-sm font-bold text-astro-primary font-serif">
                  Batch Role & Tier Elevation Summary ({modifiedUserIds.length} Changes)
                </h4>
                <p className="text-xs text-astro-muted">
                  Review all modified member accounts before final batch commit to Firestore.
                </p>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto space-y-2.5 pr-1">
              {modifiedUserIds.map((uid) => {
                const u = usersList.find((item) => item.uid === uid);
                if (!u) return null;
                const newRole = stagedRoles[uid] !== undefined ? stagedRoles[uid] : u.role;
                const newTier = stagedTiers[uid] !== undefined ? stagedTiers[uid] : (u.tier || AccountTier.GUEST);

                return (
                  <div key={uid} className="p-3 rounded-2xl bg-astro-card border border-astro-border space-y-2 text-xs">
                    <div className="flex items-center justify-between">
                      <span className="font-bold text-astro-primary font-mono">{u.email}</span>
                      <span className="text-[10px] px-2 py-0.5 rounded bg-amber-100 text-amber-800 font-bold">
                        {u.displayName || 'Member'}
                      </span>
                    </div>

                    <div className="grid grid-cols-2 gap-2 pt-1 border-t border-astro-border/60 text-[11px]">
                      <div className="p-2 rounded-xl bg-white border border-astro-border/50">
                        <span className="text-astro-muted block font-semibold mb-0.5">Role Authority</span>
                        <div className="flex items-center gap-1 font-mono">
                          <span className="text-red-700">{u.role || 'basic_user'}</span>
                          <ArrowRight className="w-3 h-3 text-astro-accent shrink-0" />
                          <span className="text-emerald-700 font-bold">{newRole}</span>
                        </div>
                      </div>

                      <div className="p-2 rounded-xl bg-white border border-astro-border/50">
                        <span className="text-astro-muted block font-semibold mb-0.5">Account Tier</span>
                        <div className="flex items-center gap-1 font-mono">
                          <span className="text-red-700">{u.tier || 'free'}</span>
                          <ArrowRight className="w-3 h-3 text-astro-accent shrink-0" />
                          <span className="text-emerald-700 font-bold">{newTier}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="flex items-center justify-end gap-3 pt-3 border-t border-astro-border shrink-0">
              <button
                type="button"
                onClick={() => setBatchModalOpen(false)}
                className="px-4 py-2 text-xs font-semibold text-astro-secondary-text hover:bg-astro-secondary-surface rounded-xl transition-colors cursor-pointer"
              >
                Continue Editing
              </button>
              <button
                id="btn-confirm-commit-all"
                type="button"
                onClick={handleCommitBatch}
                className="px-6 py-2.5 bg-amber-600 hover:bg-amber-700 text-white text-xs font-bold rounded-xl shadow-md transition-all cursor-pointer flex items-center gap-1.5"
              >
                <Save className="w-4 h-4" />
                <span>Confirm & Commit All ({modifiedUserIds.length}) Changes</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* BAN USER MODAL */}
      {banTargetUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs animate-fade-in">
          <div className="bg-astro-bg border border-astro-border rounded-2xl shadow-2xl max-w-lg w-full p-6 space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-red-100 text-red-800 flex items-center justify-center font-bold">
                <ShieldAlert className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h4 className="text-sm font-bold text-astro-primary font-serif">
                  Enforce Account Ban & Blacklist Registry
                </h4>
                <p className="text-xs text-astro-muted">
                  Target User: <span className="font-mono font-bold text-red-700">{banTargetUser.email}</span>
                </p>
              </div>
            </div>

            <div className="space-y-3 text-xs">
              <div className="space-y-1.5">
                <label className="font-bold text-astro-secondary-text block">Quick Violation Reason Presets</label>
                <div className="flex flex-wrap gap-1.5">
                  {BAN_PRESETS.map((preset) => (
                    <button
                      key={preset}
                      type="button"
                      onClick={() => setBanReason(preset)}
                      className={`px-2 py-1 rounded-lg border text-[11px] font-medium transition-all ${
                        banReason === preset
                          ? 'bg-red-600 text-white border-red-600 shadow-xs'
                          : 'bg-astro-card hover:bg-astro-border text-astro-text'
                      }`}
                    >
                      {preset}
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="font-bold text-astro-secondary-text block">Detailed Reason / Notes *</label>
                <textarea
                  rows={2}
                  value={banReason}
                  onChange={(e) => setBanReason(e.target.value)}
                  placeholder="Enter detailed reason for ban..."
                  className="w-full text-xs p-2.5 border border-[#D5C7B2] rounded-xl focus:ring-1 focus:ring-red-500 focus:outline-hidden"
                />
              </div>

              <div className="p-3 rounded-xl bg-red-50/60 border border-red-200 space-y-2">
                <span className="font-bold text-red-900 block">Blacklist Contact Identifiers</span>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={blacklistEmail}
                    onChange={(e) => setBlacklistEmail(e.target.checked)}
                    className="rounded text-red-600 focus:ring-red-500"
                  />
                  <span>Blacklist Email: <strong className="font-mono">{banTargetUser.email}</strong></span>
                </label>
                {banTargetUser.mobile && (
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={blacklistPhone}
                      onChange={(e) => setBlacklistPhone(e.target.checked)}
                      className="rounded text-red-600 focus:ring-red-500"
                    />
                    <span>Blacklist Phone: <strong className="font-mono">{banTargetUser.mobile}</strong></span>
                  </label>
                )}
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 pt-3 border-t border-astro-border">
              <button
                type="button"
                onClick={() => setBanTargetUser(null)}
                disabled={isBanning}
                className="px-4 py-2 text-xs font-semibold text-astro-secondary-text hover:bg-astro-secondary-surface rounded-xl transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleExecuteBan}
                disabled={isBanning || !banReason.trim()}
                className="flex items-center gap-2 px-5 py-2 bg-red-600 hover:bg-red-700 text-white text-xs font-bold rounded-xl shadow-md transition-all disabled:opacity-50 cursor-pointer"
              >
                <Ban className="w-4 h-4" />
                <span>{isBanning ? 'Enforcing Ban...' : 'Confirm Ban & Blacklist'}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* PREEMPTIVE BLACKLIST MODAL */}
      {preemptiveModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs animate-fade-in">
          <div className="bg-astro-bg border border-astro-border rounded-2xl shadow-2xl max-w-md w-full p-6 space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-red-100 text-red-800 flex items-center justify-center font-bold">
                <AlertOctagon className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h4 className="text-sm font-bold text-astro-primary font-serif">
                  Preemptive Security Blacklist
                </h4>
                <p className="text-xs text-astro-muted">
                  Block an unauthenticated phone number or email from future sign-ups.
                </p>
              </div>
            </div>

            <form onSubmit={handleExecutePreemptiveBlacklist} className="space-y-3">
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-astro-secondary-text block">Identifier Type</label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setPreemptiveType('phone')}
                    className={`flex-1 py-1.5 text-xs font-bold rounded-xl border transition-all ${
                      preemptiveType === 'phone'
                        ? 'bg-red-600 text-white border-red-600 shadow-xs'
                        : 'bg-astro-card text-astro-text border-astro-border'
                    }`}
                  >
                    Mobile Phone
                  </button>
                  <button
                    type="button"
                    onClick={() => setPreemptiveType('email')}
                    className={`flex-1 py-1.5 text-xs font-bold rounded-xl border transition-all ${
                      preemptiveType === 'email'
                        ? 'bg-red-600 text-white border-red-600 shadow-xs'
                        : 'bg-astro-card text-astro-text border-astro-border'
                    }`}
                  >
                    Email Address
                  </button>
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-bold text-astro-secondary-text block">
                  {preemptiveType === 'phone' ? 'Mobile Phone Number (10 digits)' : 'Email Address'} <span className="text-red-500">*</span>
                </label>
                <input
                  id="preemptive-identifier-input"
                  type={preemptiveType === 'phone' ? 'tel' : 'email'}
                  required
                  placeholder={preemptiveType === 'phone' ? 'e.g. 9876543210' : 'e.g. spammer@domain.com'}
                  value={preemptiveIdentifier}
                  onChange={(e) => setPreemptiveIdentifier(e.target.value)}
                  className="w-full text-xs p-3 border border-[#D5C7B2] rounded-xl focus:ring-2 focus:ring-red-500 focus:outline-hidden font-mono"
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-bold text-astro-secondary-text block">
                  Blacklist Reason <span className="text-red-500">*</span>
                </label>
                <input
                  id="preemptive-reason-input"
                  type="text"
                  required
                  placeholder="e.g. Known spam bot or abusive phone number"
                  value={preemptiveReason}
                  onChange={(e) => setPreemptiveReason(e.target.value)}
                  className="w-full text-xs p-3 border border-[#D5C7B2] rounded-xl focus:ring-2 focus:ring-red-500 focus:outline-hidden"
                />
              </div>

              <div className="flex items-center justify-end gap-3 pt-3 border-t border-astro-border">
                <button
                  type="button"
                  onClick={() => setPreemptiveModalOpen(false)}
                  disabled={isBlacklisting}
                  className="px-4 py-2 text-xs font-semibold text-astro-secondary-text hover:bg-astro-secondary-surface rounded-xl transition-colors"
                >
                  Cancel
                </button>
                <button
                  id="btn-confirm-blacklist"
                  type="submit"
                  disabled={isBlacklisting || !preemptiveIdentifier.trim()}
                  className="flex items-center gap-2 px-5 py-2 bg-red-600 hover:bg-red-700 text-white text-xs font-bold rounded-xl shadow-md transition-all disabled:opacity-50 cursor-pointer"
                >
                  <Ban className="w-4 h-4" />
                  <span>{isBlacklisting ? 'Blacklisting...' : 'Add to Blacklist'}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
